import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useEffect, useMemo, useState } from 'react';
import { LIMITS } from '@bunker/shared';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Modal, Spinner } from '@heroui/react';
import { api, type GlobalSearchHit } from '@/lib/api';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { emitAppEvent } from '@/lib/appEvents';
import { messagePreviewText, shortTimeLabel } from '@/lib/messagePreview';
import { directChannelLabel } from '@/components/channelDrawer/primitives';
import { HashIcon, SearchIcon, UsersIcon } from '@/components/icons';
import { LoadError } from '@/components/ui/LoadError';

/**
 * Global message search. A debounced query hits /api/me/search (ACL-filtered
 * server-side); each hit shows its channel + author + a content preview. Click
 * navigates to the channel/DM and asks the message list to scroll to the hit.
 */
export function GlobalSearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const selectGuild = useDataStore((s) => s.selectGuild);
  const selectChannel = useDataStore((s) => s.selectChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const dmChannels = useDataStore((s) => s.dmChannels);
  const myId = useAuthStore((s) => s.user?.id ?? null);

  // Reset on open; debounce keystrokes so we don't fire a request per char.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const enabled = open && debounced.length >= LIMITS.search.minQueryLength;
  const search = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: () => api.searchGlobal(debounced),
    enabled,
    retry: 1,
  });

  const hits = useMemo(() => search.data?.messages ?? [], [search.data]);

  function navigate(hit: GlobalSearchHit) {
    if (!hit.channel) return;
    const { id, type, guildId } = hit.channel;
    if (type === 'DM' || type === 'GROUP_DM') {
      selectDirectChannel(id);
    } else {
      if (guildId) selectGuild(guildId);
      selectChannel(id);
    }
    // Best-effort: if the message is already loaded, the list scrolls to it.
    setTimeout(() => emitAppEvent('chat:scroll-to-message', { messageId: hit.id }), 60);
    onClose();
  }

  return (
    <Modal.Backdrop className="app-viewport-overlay" isOpen={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Container className="app-panel-container" size="lg" placement="top">
        <Modal.Dialog aria-label={t('sidebar.searchMessages')} className="app-panel-dialog app-search-dialog overflow-hidden p-0">
          <div className="app-search-header flex shrink-0 items-center gap-3 px-5 py-4">
            <SearchIcon className="h-5 w-5 shrink-0 text-ink-muted" />
            <input
              aria-label={t('sidebar.searchMessages')}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="block min-h-11 min-w-0 flex-1 bg-transparent text-base text-ink placeholder:text-ink-muted focus:outline-none"
            />
            {search.isFetching && <Spinner size="sm" />}
            <Modal.CloseTrigger className="app-search-close" />
          </div>
          <div className="border-t border-separator" />
          <div className="app-panel-results scrollbar-thin min-h-0 overflow-y-auto py-1">
            {!enabled && (
              <p role="status" className="px-5 py-6 text-center text-sm text-ink-muted">
                {t('cmod.search.minChars')}
              </p>
            )}
            {enabled && search.isLoading && (
              <p role="status" className="px-5 py-6 text-center text-sm text-ink-muted">{t('common.loading')}</p>
            )}
            {enabled && search.isError && (
              <LoadError error={search.error} onRetry={() => void search.refetch()} />
            )}
            {enabled && !search.isLoading && !search.isError && hits.length === 0 && (
              <p role="status" className="px-5 py-6 text-center text-sm text-ink-muted">
                {t('cmod.search.empty')}
              </p>
            )}
            {enabled && !search.isError && hits.map((hit) => (
              <SearchHitRow key={hit.id} hit={hit} myId={myId} dmChannels={dmChannels} onClick={() => navigate(hit)} />
            ))}
          </div>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function SearchHitRow(props: {
  hit: GlobalSearchHit;
  myId: string | null;
  dmChannels: ReturnType<typeof useDataStore.getState>['dmChannels'];
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const { hit } = props;
  const author = hit.author;
  const authorName = author.displayName || author.username;
  const preview = messagePreviewText(hit, { selfId: props.myId, withAuthor: false });
  const time = shortTimeLabel(hit.createdAt);

  // Channel label: #name for guild text channels, the DM label for DMs.
  let channelLabel = hit.channel?.name ? `#${hit.channel.name}` : t('common.channelLabel');
  let ChannelIcon = HashIcon;
  if (hit.channel && (hit.channel.type === 'DM' || hit.channel.type === 'GROUP_DM')) {
    ChannelIcon = UsersIcon;
    const dm = props.dmChannels[hit.channel.id];
    channelLabel = dm ? directChannelLabel(dm, props.myId) : t('dm.title');
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className="flex min-h-11 w-full items-start gap-3 rounded-lg px-5 py-2.5 text-left transition-colors hover:bg-accent/8 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent"
    >
      <IdentityAvatar id={author.id} name={authorName} src={author.avatarUrl} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-ink-muted">
          <ChannelIcon className="h-3 w-3 shrink-0" />
          <span className="truncate">{channelLabel}</span>
          <span className="opacity-50">·</span>
          <span className="min-w-0 truncate">{authorName}</span>
          <span className="ml-auto shrink-0 tabular-nums">{time}</span>
        </span>
        <span className="mt-0.5 block truncate text-sm text-ink">{preview || '—'}</span>
      </span>
    </button>
  );
}
